import { FontAwesome } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { useDeleteAchCheck } from '@/api/hooks/paymentAccountsHooks';
import { TokenizedACHData } from '@/app/(app)/paymentMethods/achList';
import { npoIcon } from '@/assets/images/icons';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { i18n } from '@/utils/i18n';

import DotsLoader from '../DotsLoader';
import ConfirmWarningModal from '../modals/ConfirmWarningModal';
import Text from '../Text';

interface TokenizedACHViewProps {
  tokenizedAch: TokenizedACHData;
  showDelete?: boolean;
  showChange?: boolean;
  onPress?: () => void;
  onDelete?: () => void;
}

const TokneizedACHView = ({
  tokenizedAch,
  showDelete = true,
  showChange = false,
  onPress,
  onDelete
}: TokenizedACHViewProps) => {
  const { showSuccess, showError } = useEndFlowModal();

  const { mutateAsync: deleteAchCheck, isPending } = useDeleteAchCheck();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleDelete = () => {
    setShowConfirmationModal(false);
    deleteAchCheck(tokenizedAch.check_key ?? '', {
      onSuccess: () => {
        onDelete && onDelete();
        showSuccess({ title: i18n.t('end_flow.success_title'), message: i18n.t('payment_methods.ach_deleted') });
      },
      onError: (e) => {
        console.error(e);
        showError({ message: e.message ?? i18n.t('payment_methods.ach_delete_error') });
      }
    });
  };

  const itemView = () => {
    if (isPending)
      return (
        <View style={styles.loader}>
          <DotsLoader />
        </View>
      );

    return (
      <View style={styles.container}>
        <Image source={npoIcon} style={styles.logo} contentFit="contain" />
        <View style={styles.dataContainer}>
          <Text fontWeight="700" style={styles.number}>
            {i18n.t('credit_cards.finished_in', {
              NUMBER: tokenizedAch.account_number_last_four_digits
            })}
          </Text>
          <Text style={styles.nameOnCheck}>{tokenizedAch.name_on_check}</Text>
        </View>
        {showChange && (
          <View style={styles.changeContainer}>
            <FontAwesome name="exchange" size={24} color="gray" />
            <Text fontWeight="700" style={styles.change}>
              {i18n.t('mix.change')}
            </Text>
          </View>
        )}
        {showDelete && (
          <TouchableOpacity onPress={() => setShowConfirmationModal(true)}>
            <Text fontWeight="700" style={styles.delete}>
              {i18n.t('mix.delete')}
            </Text>
          </TouchableOpacity>
        )}
        <ConfirmWarningModal
          message={i18n.t('payment_methods.ach_delete_confirm')}
          showPicker={showConfirmationModal}
          loading={isPending}
          onClosePicker={() => setShowConfirmationModal(false)}
          onConfirmTapped={handleDelete}
        />
      </View>
    );
  };

  if (onPress) {
    return <TouchableOpacity onPress={onPress}>{itemView()}</TouchableOpacity>;
  } else return itemView();
};

export default TokneizedACHView;

const styles = StyleSheet.create({
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5
  },

  logo: {
    height: 45,
    width: 45
  },

  dataContainer: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingStart: 10
  },

  number: {
    fontSize: 16,
    color: 'black'
  },

  nameOnCheck: {
    fontSize: 16
  },

  changeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2.5
  },

  change: {
    color: 'gray'
  },

  delete: {
    color: 'red'
  }
});
